import React from 'react';
import { InputNumber, Typography, Space, Card, DatePicker, theme } from 'antd';
import { 
  TrophyOutlined, 
  CalendarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;

export interface TournamentConfigValues {
  points1st: number;
  points2nd: number;
  points3rd: number;
  enableThirdPlace: boolean;
  scheduledStartDate: string | null; // ISO 8601 date string or null for immediate start
  responseDeadlineDays: number;
  responseDeadlineHours: number;
}

export const DEFAULT_TOURNAMENT_CONFIG: TournamentConfigValues = {
  points1st: 100,
  points2nd: 50,
  points3rd: 25,
  enableThirdPlace: false,
  scheduledStartDate: null,
  responseDeadlineDays: 1,
  responseDeadlineHours: 0,
};

interface TournamentConfigProps {
  config: TournamentConfigValues;
  onChange: (config: TournamentConfigValues) => void;
}

const TournamentConfig: React.FC<TournamentConfigProps> = ({ config, onChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const handleScheduledDateChange = (date: Dayjs | null) => {
    onChange({
      ...config,
      scheduledStartDate: date ? date.toISOString() : null,
    });
  };

  // Disable dates in the past
  const disabledDate = (current: Dayjs) => {
    return current && current < dayjs().startOf('day');
  };

  return (
    <Card 
      style={{ 
        marginBottom: 16,
        borderRadius: 8
      }}
      title={
        <Space>
          <TrophyOutlined style={{ fontSize: 16 }} />
          <Text strong>{t('tournament.configuration', 'Postavke turnira')}</Text>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Scheduled Start Date */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <CalendarOutlined style={{ fontSize: 14 }} />
              <Text strong style={{ fontSize: 13 }}>{t('tournament.scheduledStart', 'Scheduled Start')}</Text>
            </div>
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              placeholder={t('tournament.selectStartDateTime', 'Select start date & time (optional)')}
              value={config.scheduledStartDate ? dayjs(config.scheduledStartDate) : null}
              onChange={handleScheduledDateChange}
              disabledDate={disabledDate}
              style={{ width: '100%' }}
              allowClear
            />
            <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic', display: 'block', marginTop: 6 }}>
              {config.scheduledStartDate 
                ? t('tournament.scheduledHint', 'Tournament will remain in "Scheduled" status until this time.')
                : t('tournament.immediateStartHint', 'Leave empty to start the tournament immediately after creation.')
              }
            </Text>
          </div>
          
          {/* Response Deadline */}
          <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <ClockCircleOutlined style={{ fontSize: 14 }} />
              <Text strong style={{ fontSize: 13 }}>
                {t('tournament.responseDeadline', 'Response Deadline per Round')} <span style={{ color: 'red' }}>*</span>
              </Text>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <Space>
                <InputNumber
                  min={0}
                  max={30}
                  value={config.responseDeadlineDays}
                  onChange={(value) => onChange({ ...config, responseDeadlineDays: value ?? 0 })}
                  style={{ width: 60 }}
                />
                <Text style={{ fontSize: 12 }}>{t('tournament.days', 'days')}</Text>
              </Space>
              <Space>
                <InputNumber
                  min={0}
                  max={23}
                  value={config.responseDeadlineHours}
                  onChange={(value) => onChange({ ...config, responseDeadlineHours: value ?? 0 })}
                  style={{ width: 60 }}
                />
                <Text style={{ fontSize: 12 }}>{t('tournament.hours', 'hours')}</Text>
              </Space>
            </div>
            <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic', display: 'block', marginTop: 8 }}>
              {t('tournament.responseDeadlineHint', 'Time each participant has to complete their match before automatic forfeit.')}
            </Text>
          </div>
      </div>
    </Card>
  );
};

export default TournamentConfig;
